import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { motion } from "framer-motion";

// Importing modal components
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../../modals/AnimatedModal";

// Importing common helpers and validators
import {
  ERROR_REQUIRED_FIELDS,
  STRING_NOT_NULL_HELPER,
} from "../../../common/helpersTxt";

// Importing frames and APIs
import api from "../../../store/services/api";
import {
  cfopFrame,
  cofins_situacao_tributaria_frame,
  icms_origem_frame,
  icms_situacao_tributaria_frame,
  pis_situacao_tributaria_frame,
} from "../../../common/frames";

// Importing components
import MoneyTextField from "../../Inputs/MoneyTextField";
import ItemRowWithCheckbox from "./itemRowWithCheckbox";
import ItemFiscalDataForm from "../fiscal/ItemFiscalDataForm";
import CollapseAlert from "../../alerts/CollapseAlert";

// Material UI components
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Radio from "@mui/material/Radio";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import { OutlinedInput } from "@mui/material";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import InventoryIcon from "@mui/icons-material/Inventory";
import TabPanel from "../../TabPanel/TabPanel";

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
};

const buttonVariants = {
  hover: { scale: 1.05 },
  tap: { scale: 0.95 },
};

export default function ComplementModal({
  show,
  closeModal,
  complement,
  createComplement,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [page, setPage] = useState(1);
  const [tabValue, setTabValue] = useState(0);

  const [productTitle, setProductTitle] = useState("");
  const [productsList, setProductsList] = useState([]);
  const [complementsListToDelete, setComplementsListToDelete] = useState([]);

  const [submitted, setSubmitted] = useState(false);

  const [title, setTitle] = useState(complement?.title || "");
  const [isTitleValid, setIsTitleValid] = useState(false);

  // Format the initial price value for MoneyTextField
  const formatInitialPrice = (priceValue) => {
    if (!priceValue) return "";
    if (typeof priceValue === "number") {
      return priceValue.toFixed(2).replace(".", ",");
    }
    return priceValue.toString().replace(".", ",");
  };

  const [price, setPrice] = useState(
    formatInitialPrice(complement?.price || "")
  );
  const [fiscal_code, setFiscalCode] = useState(complement?.fiscal_code || "");
  const [ifoodExternalCode, setIfoodExternalCode] = useState(
    complement?.ifood_pos_code || ""
  );
  const [cfop_code, setCfopCode] = useState(cfopFrame[0]);
  const [icmsSituacaoTributariaState, setIcmsSituacaoTributariaState] =
    useState(icms_situacao_tributaria_frame[0]);
  const [icmsOrigemState, setIcmsOrigemState] = useState(icms_origem_frame[0]);
  const [icmsValue, setIcmsValue] = useState(0);

  const [cofinsSituacaoTributariaState, setCofinsSituacaoTributariaState] =
    useState(cofins_situacao_tributaria_frame[0]);
  const [cofinsValue, setCofinsValue] = useState(0);

  const [pisSituacaoTributariaState, setPisSituacaoTributariaState] = useState(
    pis_situacao_tributaria_frame[0]
  );
  const [pisValue, setPisValue] = useState(0);

  const [unit, setUnit] = useState(complement?.unit || "UN");

  const [width, setWidth] = useState(complement?.width || 0);
  const [length, setLength] = useState(complement?.length || 0);
  const [height, setHeight] = useState(complement?.height || 0);
  const [weight, setWeight] = useState(complement?.weight || 0);

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState(ERROR_REQUIRED_FIELDS);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Convert price from formatted string to float
  const convertPriceToFloat = (formattedPrice) => {
    if (!formattedPrice) return 0;

    // Remove R$ and any non-numeric characters except comma
    const cleanedPrice = formattedPrice
      .replace(/R\$\s*/g, "")
      .replace(/\./g, "")
      .trim();

    // Replace comma with dot for parsing
    const numericPrice = cleanedPrice.replace(",", ".");

    return parseFloat(numericPrice || 0);
  };

  useEffect(() => {
    setTimeout(async () => {
      await loadData();
    }, 10);
  }, []);

  const loadData = async () => {
    const filters = `?page=${page}`;
    let treatedProductComplements = [];
    if (complement) {
      const { data } = await api.get("/complements", {
        params: { pcid: complement.complement_id },
      });
      treatedProductComplements = data.map((product) => {
        product.existed = true;
        product.checked = true;
        return product;
      });
      setIsEditing(true);
    }

    const { data } = await api.get("/products" + filters);
    const newData = data.filter(
      (item) =>
        !treatedProductComplements.some(
          (elem) => elem.product === item.product_id
        )
    );
    setProductsList([...treatedProductComplements, ...newData]);
  };

  const handleSelectedUnit = (event) => {
    setUnit(event.target.value);
  };

  const searchProducts = async (event) => {
    try {
      event.preventDefault();
      setLoading(true);
      setPage(1);
      const filters = `?page=${page}&title=${productTitle}`;
      const { data } = await api.get("/products" + filters);
      setProductsList(data);
    } catch (e) {
      setErrorMessage("Erro ao buscar produtos");
      setOpenErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const addComplementsToDelete = () => {
    const complementsToAdd = productsList.filter(
      (product) => !product.checked && product.existed
    );
    setComplementsListToDelete((prevList) => [
      ...prevList,
      ...complementsToAdd,
    ]);
  };

  const handleIsChecked = (value, index) => {
    const _productsList = productsList;
    _productsList[index].checked = value;
    setProductsList(_productsList);
    addComplementsToDelete();
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    // Validation could be added here similar to the product modal

    // Convert the formatted price string to float before submitting
    const priceAsFloat = convertPriceToFloat(price);

    createComplement(isEditing, {
      complement_id: isEditing ? complement.complement_id : uuidv4(),
      title: title,
      fiscal_code: fiscal_code,
      price: priceAsFloat.toFixed(2),
      width: parseFloat(width || 0).toFixed(2),
      length: parseFloat(length || 0).toFixed(2),
      height: parseFloat(height || 0).toFixed(2),
      weight: parseFloat(weight || 0).toFixed(2),
      deleted_at: null,
      productsComplementsToCreate: productsList.filter(
        (product) => product.checked && !product.existed
      ),
      complementsToDelete: complementsListToDelete,
    });
    closeModal();
  };

  if (loading && !productsList.length) {
    return (
      <AnimatedModal isOpen={show} onClose={closeModal} zIndex={5000}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <CircularProgress />
        </Box>
      </AnimatedModal>
    );
  }

  return (
    <AnimatedModal isOpen={show} onClose={closeModal} zIndex={5000}>
      <ModalHeader
        title={isEditing ? complement.title : "Novo Complemento"}
        icon={LocalOfferIcon}
        onClose={closeModal}
      />

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            "& .MuiTab-root": {
              minHeight: "64px",
              fontWeight: 600,
            },
          }}
        >
          <Tab
            icon={<LocalOfferIcon />}
            label="Informações"
            id="complement-tab-0"
            aria-controls="complement-tabpanel-0"
          />
          <Tab
            icon={<ReceiptIcon />}
            label="Fiscal"
            id="complement-tab-1"
            aria-controls="complement-tabpanel-1"
          />
          <Tab
            icon={<InventoryIcon />}
            label="Produtos"
            id="complement-tab-2"
            aria-controls="complement-tabpanel-2"
          />
        </Tabs>
      </Box>

      <ModalContent padding={0}>
        {/* Basic Info Tab */}
        <TabPanel value={tabValue} index={0}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.light, 0.05),
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    variant="outlined"
                    id="new_complement_name"
                    name="title"
                    label="Nome do Complemento"
                    fullWidth
                    required
                    error={!isTitleValid && submitted}
                    value={title}
                    helperText={
                      !isTitleValid && submitted ? STRING_NOT_NULL_HELPER : ""
                    }
                    onChange={(event) => {
                      setTitle(event.target.value);
                    }}
                    InputProps={{
                      sx: { borderRadius: 2 },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MoneyTextField
                    id="complement-price"
                    label="Preço"
                    value={price}
                    setValue={setPrice}
                    required={true}
                    fullWidth={true}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider>
                    <Chip
                      label="Unidade de Medida"
                      color="primary"
                      variant="outlined"
                    />
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      aria-labelledby="complement-units-group"
                      name="complement-units"
                      value={unit}
                      onChange={handleSelectedUnit}
                    >
                      <FormControlLabel
                        value="UN"
                        control={
                          <Radio
                            sx={{
                              "&.Mui-checked": {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label="Unidade"
                      />
                      <FormControlLabel
                        value="KG"
                        control={
                          <Radio
                            sx={{
                              "&.Mui-checked": {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label="Quilograma (Kg)"
                      />
                      <FormControlLabel
                        value="L"
                        control={
                          <Radio
                            sx={{
                              "&.Mui-checked": {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label="Litro (L)"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </motion.div>
        </TabPanel>

        {/* Fiscal Tab */}
        <TabPanel value={tabValue} index={1}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <ItemFiscalDataForm
              fiscalCode={fiscal_code}
              setFiscalCode={setFiscalCode}
              cofinsSituacaoTributariaState={cofinsSituacaoTributariaState}
              setCofinsSituacaoTributariaState={
                setCofinsSituacaoTributariaState
              }
              cofinsValue={cofinsValue}
              setCofinsValue={setCofinsValue}
              pisSituacaoTributariaState={pisSituacaoTributariaState}
              setPisSituacaoTributariaState={setPisSituacaoTributariaState}
              pisValue={pisValue}
              setPisValue={setPisValue}
              icmsOrigemState={icmsOrigemState}
              setIcmsOrigemState={setIcmsOrigemState}
              icmsSituacaoTributariaState={icmsSituacaoTributariaState}
              setIcmsSituacaoTributariaState={setIcmsSituacaoTributariaState}
              icmsValue={icmsValue}
              setIcmsValue={setIcmsValue}
              cfopCode={cfop_code}
              setCfopCode={setCfopCode}
              ifoodExternalCode={ifoodExternalCode}
              setIfoodExternalCode={setIfoodExternalCode}
            />
          </motion.div>
        </TabPanel>

        {/* Products Tab */}
        <TabPanel value={tabValue} index={2}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Box
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={searchProducts}
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
              }}
            >
              <FormControl
                sx={{ flex: 1 }}
                variant="outlined"
                onSubmit={searchProducts}
              >
                <InputLabel htmlFor="search-products-input">
                  Pesquisar produtos
                </InputLabel>
                <OutlinedInput
                  id="search-products-input"
                  type="text"
                  label="Pesquisar produtos"
                  onChange={(event) => {
                    setProductTitle(event.target.value);
                  }}
                  sx={{ borderRadius: 2 }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="search products"
                        onClick={searchProducts}
                        edge="end"
                        type="submit"
                        color="primary"
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
                maxHeight: "500px",
                overflow: "auto",
              }}
            >
              {productsList.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    opacity: 0.7,
                  }}
                >
                  <InventoryIcon sx={{ fontSize: 48, opacity: 0.6, mb: 2 }} />
                  <Typography variant="body1">
                    Nenhum produto encontrado
                  </Typography>
                </Box>
              ) : (
                productsList.map((product, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Box
                      sx={{
                        mb: 1,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.light, 0.1),
                          transform: "translateX(5px)",
                        },
                      }}
                    >
                      <ItemRowWithCheckbox
                        isSelected={product.checked}
                        setIsSelected={handleIsChecked}
                        product={product}
                        itemIndex={index}
                      />
                      <Divider />
                    </Box>
                  </motion.div>
                ))
              )}
            </Box>
          </motion.div>
        </TabPanel>
      </ModalContent>

      <ModalFooter>
        <Box sx={{ width: "100%" }}>
          <CollapseAlert
            setOpenErrorAlert={setOpenErrorAlert}
            errorMessage={errorMessage}
            openErrorAlert={openErrorAlert}
          />

          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Button
              variant="outlined"
              onClick={closeModal}
              sx={{ borderRadius: 2 }}
            >
              Cancelar
            </Button>

            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                startIcon={isEditing ? <EditIcon /> : <AddIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                }}
              >
                {isEditing ? "Editar Complemento" : "Criar Complemento"}
              </Button>
            </motion.div>
          </Stack>
        </Box>
      </ModalFooter>
    </AnimatedModal>
  );
}
